import { useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { v4 as uuidv4 } from "uuid";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import { Button } from "@/components/ui/button";
import { ImagePlus, Loader2, X } from "lucide-react";

const MAX_BYTES = 1.5 * 1024 * 1024; // 1.5 MB
const ALLOWED_EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
};

interface ImageUploadFieldProps {
  bucket: string;
  storeId: string;
  value: string | null;
  onUploaded: (url: string | null) => void;
}

/**
 * Uploads to `${bucket}/${storeId}/${uuid}.${ext}` (matches the storage RLS
 * write policy, which checks the first folder segment against the caller's
 * store_id). Refuses anything over 1.5 MB or not jpg/png/gif with an inline
 * message rather than attempting the upload.
 */
export function ImageUploadField({ bucket, storeId, value, onUploaded }: ImageUploadFieldProps) {
  const getSupabaseClient = useSupabaseClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File | undefined) => {
    setError(null);
    if (!file) return;

    const ext = ALLOWED_EXT[file.type];
    if (!ext) {
      setError("Only JPG, PNG, or GIF images are allowed.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("Image must be smaller than 1.5 MB.");
      return;
    }

    setUploading(true);
    try {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const path = `${storeId}/${uuidv4()}.${ext}`;
      const { error: uploadError } = await sb.storage.from(bucket).upload(path, file, {
        contentType: file.type,
        upsert: false,
      });
      if (uploadError) throw uploadError;

      const { data } = sb.storage.from(bucket).getPublicUrl(path);
      onUploaded(data.publicUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed. Try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {value ? (
        <div className="relative inline-block">
          <img src={value} alt="Promotion" className="h-24 w-24 rounded-lg border border-border object-cover" />
          <button
            type="button"
            onClick={() => onUploaded(null)}
            className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-foreground text-background"
            aria-label="Remove image"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          className="h-11"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-2 h-4 w-4" />}
          {uploading ? "Uploading…" : "Add image"}
        </Button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      {error && <p className="mt-1 text-xs text-status-danger">{error}</p>}
    </div>
  );
}
